// The one stepper of a rental SO: from the Venio order to a signed contract. Each pill
// is a step and shows only its own content below. Steps that happen outside VERP are
// ticked off by hand; the rest follow the order's own status.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../config/api';

export const STEP_LABEL = {
  so_received: 'ข้อมูล SO',
  asset_form: 'ขอโอนเป็นทรัพย์สิน',
  asset_adjust: 'รออนุมัติโอน',
  contract: 'ทำสัญญา',
  signed: 'ลงนาม'
};
const STATE_LABEL = { done: 'เสร็จแล้ว', doing: 'กำลังดำเนินการ', todo: 'ยังไม่ทำ', problem: 'มีปัญหา' };
const ICON = { done: 'bi-check-lg', doing: 'bi-hourglass-split', problem: 'bi-exclamation-lg', todo: 'bi-circle' };

const fmt = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** Loads the SO's journey and ticks/unticks the hand-done steps. */
export function useJourney(orderId) {
  const [journey, setJourney] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    axios.get(`${API_URL}/rental/${orderId}/journey`)
      .then(resp => setJourney(resp.data))
      .catch(() => setJourney(null));
  }, [orderId]);

  useEffect(() => { reload(); }, [reload]);

  const toggle = async (step) => {
    setBusy(true);
    try {
      const url = `${API_URL}/rental/${orderId}/steps/${step.key}`;
      const resp = step.state === 'done' ? await axios.delete(url) : await axios.post(url);
      setJourney(resp.data);
      return resp.data;
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกขั้นตอนไม่สำเร็จ');
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { journey, busy, reload, toggle };
}

/** The pills. Clicking one asks the page to show that step. */
export const JourneyBar = ({ journey, active, onSelect }) => {
  if (!journey?.steps) return null;
  return (
    <nav className="sj" aria-label="ขั้นตอนของ SO">
      <ol className="sj__pills">
        {journey.steps.map((s, i) => (
          <li key={s.key} className="sj__item">
            <button
              type="button"
              className={`sj__pill sj__pill--${s.state}${s.current ? ' is-current' : ''}${s.key === active ? ' is-open' : ''}`}
              aria-current={s.key === active ? 'step' : undefined}
              onClick={() => onSelect(s.key)}
            >
              <span className="sj__dot" aria-hidden="true">
                <i className={`bi ${s.current && s.state === 'todo' ? 'bi-arrow-right' : ICON[s.state]}`}></i>
              </span>
              {STEP_LABEL[s.key] || s.title}
            </button>
            {i < journey.steps.length - 1 && <i className="bi bi-chevron-right sj__sep" aria-hidden="true"></i>}
          </li>
        ))}
      </ol>
    </nav>
  );
};

/** Content of a step done by hand outside VERP: what to do, then "done". */
export const ManualStepPanel = ({ step, title, children, busy, onToggle, onNext, toggleLabel }) => {
  if (!step) return null;
  const done = step.state === 'done';
  return (
    <section className="rs-card sj-panel">
      <div className="sj-panel__head">
        <h2 className="rs-card__title">{title}</h2>
        <span className={`sj__state sj__state--${step.state}`}>{STATE_LABEL[step.state]}</span>
        {done && step.at && <span className="sj__meta">{fmt(step.at)}{step.by ? ` · ${step.by}` : ''}</span>}
      </div>
      <div className="sj-panel__body">{children}</div>
      <div className="sj-panel__actions">
        {step.link && !done && (
          <a className="rs-btn rs-btn--ghost" href={step.link} target="_blank" rel="noopener noreferrer">
            {step.linkLabel} <i className="bi bi-box-arrow-up-right"></i>
          </a>
        )}
        <button type="button" className={`rs-btn ${done ? 'rs-btn--ghost' : 'rs-btn--primary'}`} disabled={busy} onClick={() => onToggle(step)}>
          {done ? (toggleLabel?.[1] || 'ยกเลิกการทำเครื่องหมาย') : (toggleLabel?.[0] || 'ทำเรียบร้อยแล้ว')}
        </button>
        {done && onNext && (
          <button type="button" className="rs-btn rs-btn--primary" onClick={onNext}>
            ขั้นตอนถัดไป <i className="bi bi-arrow-right"></i>
          </button>
        )}
      </div>
    </section>
  );
};
